/**
 *  Created by piotr@revisual.io on 29/12/2025
 */
import { Chart } from "react-google-charts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useReportsStore } from "../../../../hooks/redux/reports";
import appConfig from "../../../../appConfig";
import { formatDate } from "../../../../helpers/date";
import { useUserStore } from "../../../../hooks/redux/user";
import Dropdown from "../../component/UI/Dropdown";
import Card from "../../theme/untitled/Card/Card";
import EmptyState from "../../theme/untitled/EmptyState/EmptyState";
import Icon from "../../theme/untitled/icons/Icon";
import { WT_LABELS } from "../../consts/widget";

const barColor = "#B146B3";

const chartOptions = {
  legend: { position: "none" },
  series: {
    0: { color: barColor, axis: "views" },
  },
  axes: {
    y: {
      views: { label: "views" },
    },
  },
  vAxis: {
    minorGridlines: { count: 0 },
    format: "0",
    viewWindow: { min: 0 }, // <-- force axis to start at 0
    minValue: 0, // <-- belt-and-suspenders for older google-charts versions
  },
  annotations: {
    alwaysOutside: true,
    textStyle: { fontSize: 12, auraColor: "none" },
  },
  chartArea: { width: "90%" },
};
const DropdownOptionsLabels = {
  all: "All widgets types",
  calendar: "Calendar widgets",
  alert: "Event alert widget",
};

const aggregateAllWidgetTypes = (data) => {
  const byDate = new Map();

  for (const row of data) {
    const key = row.bucket_start;
    byDate.set(key, (byDate.get(key) || 0) + row.total_views);
  }

  return Array.from(byDate, ([bucket_start, total_views]) => ({
    bucket_start,
    total_views,
  }));
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
export const Stats = () => {
  const { reports, fetchDashboardStats } = useReportsStore();
  const [selectedDropdownOption, setSelectedDropdownOption] = useState("all");

  const onDropdownOptionChange = useCallback(
    (option) => (e) => {
      setSelectedDropdownOption(option);
    },
    [setSelectedDropdownOption]
  );

  const dropdownOptions = useMemo(
    () =>
      Object.keys(DropdownOptionsLabels).map((key) => ({
        label: DropdownOptionsLabels[key],
        onClick: onDropdownOptionChange(key),
      })),
    [onDropdownOptionChange]
  );

  useEffect(() => {
    if (!reports.fetchDashboardStatsSuccess) {
      fetchDashboardStats();
    }
  }, [reports.fetchDashboardStatsSuccess]);

  const data = useMemo(() => {
    const _result =
      selectedDropdownOption === "all"
        ? aggregateAllWidgetTypes(reports.dashboardStatsCollection)
        : reports.dashboardStatsCollection.filter(
            (item) => item.widget_type === selectedDropdownOption
          );
    const result = _result.map((item) => {
      const date = item.bucket_start.split(" ")[0];

      const formattedDate = formatDate(new Date(date), {
        month: "short",
        day: "numeric",
        weekday: "short",
      });

      return [formattedDate, item.total_views];
    });

    result.unshift(["Timestamp 1 day", "Views"]);

    return result;
  }, [reports.dashboardStatsCollection, selectedDropdownOption]);

  const hasWidgetViewsInLast7Days = useMemo(
    () =>
      (selectedDropdownOption === "all"
        ? aggregateAllWidgetTypes(reports.dashboardStatsCollection)
        : reports.dashboardStatsCollection.filter(
            (item) => item.widget_type === selectedDropdownOption
          )
      )
        .map((i) => i.total_views)
        .reduce((a, i) => a + i, 0) > 0,
    [reports.dashboardStatsCollection, selectedDropdownOption]
  );

  const content = hasWidgetViewsInLast7Days ? (
    <div>
      <Chart
        // Note the usage of Bar and not BarChart for the material version
        chartType="ColumnChart"
        data={data}
        options={chartOptions}
        width={"100%"}
      />
    </div>
  ) : (
    <EmptyState
      icon={<Icon name="BarChart01" size={24} />}
      title="No views yet"
      body={`No ${
        selectedDropdownOption === "all"
          ? ""
          : WT_LABELS[selectedDropdownOption].toLowerCase() + " "
      }widget views in the last 7 days.`}
    />
  );

  return (
    <Card>
      <Card.Header
        title="Last 7 days performance"
        subtitle="Widget views, last 7 days."
      >
        {hasWidgetViewsInLast7Days && (
          <Dropdown
            label={DropdownOptionsLabels[selectedDropdownOption]}
            options={dropdownOptions}
          />
        )}
      </Card.Header>
      <Card.Body>{content}</Card.Body>
    </Card>
  );
};

export default Stats;
